import axios, {
    type AxiosRequestConfig,
    type AxiosError,
    type AxiosResponse,
    AxiosInstance,
    CancelTokenSource,
} from "axios";
import useGlobalStore from "@/store/global.c";
import {createBrowserHistory} from "history";
import {useAccountStore} from "@/store/account.c";
import {BaseVO} from "@/model/global";
import {get} from "lodash";

const source: CancelTokenSource = axios.CancelToken.source();
const history = createBrowserHistory();
const {VITE_BASE_URL, VITE_SIGNIN_PAGE} = import.meta.env;

export enum ResultEnum {
    SUCCESS = 0,
    ERROR = 7,
}

// 创建 axios 实例
const axiosInstance: AxiosInstance = axios.create({
    baseURL: VITE_BASE_URL,
    timeout: 50000,
    cancelToken: source.token,
    headers: {"Content-Type": "application/json;charset=utf-8"},
});

// 请求拦截
axiosInstance.interceptors.request.use(
    (config) => {
        // 在请求被发送之前做些什么
        const token = useAccountStore.getState().token;
        if (token) {
            config.headers["x-token"] = token;
        }
        return config;
    },
    (error) => {
        // 请求错误时做些什么
        return Promise.reject(error);
    },
);

// 响应拦截
axiosInstance.interceptors.response.use(
    (response) => {
        const data = response.data;
        const responseType = response.request?.responseType
        if (responseType === "blob" || responseType === "arraybuffer") return data
        const {setToast} = useGlobalStore.getState().actions;

        switch (data.code) {
            case ResultEnum.SUCCESS :
                // 本系统采用 code === 0 来表示没有业务错误
                return data
            case 401:
                return signout()
            default:
                setToast({
                    message: data.msg,
                    variant: "standard",
                    open: true,
                    severity: "error",
                    duration: 2,
                })
                return data
        }
    },
    (error: AxiosError<string>) => {
        const status = get(error, "response.status")
        const message = get(error, "response.data.message")
        const {setToast} = useGlobalStore.getState().actions;
        switch (status) {
            case 400:
                error.message = "请求错误"
                break
            case 401:
                // Token 过期时
                error.message = message || "未授权"
                signout()
                break
            case 403:
                error.message = message || "拒绝访问"
                break
            case 404:
                error.message = "请求地址出错"
                break
            case 408:
                error.message = "请求超时"
                break
            case 500:
                error.message = "服务器内部错误"
                break
            case 501:
                error.message = "服务未实现"
                break
            case 502:
                error.message = "网关错误"
                break
            case 503:
                error.message = "服务不可用"
                break
            case 504:
                error.message = "网关超时"
                break
            case 505:
                error.message = "HTTP 版本不受支持"
                break
        }
        if (error.code == "ECONNABORTED") {
            error.message = "网络连接超时"
        }
        setToast({
            message: error.message,
            variant: "standard",
            open: true,
            severity: "error",
            duration: 2,
        });
        return Promise.reject(error);
    },
);

const signout = () => {
    const {pathname} = window.location;
    if (pathname != VITE_SIGNIN_PAGE) {
        useAccountStore.getState().actions.clearAccount();
        history.replace(VITE_SIGNIN_PAGE);
    }
}

class http {
    get<T = any>(config: AxiosRequestConfig): Promise<T> {
        return this.request({...config, method: "GET"});
    }

    post<T = any>(config: AxiosRequestConfig): Promise<T> {
        return this.request({...config, method: "POST"});
    }

    put<T = any>(config: AxiosRequestConfig): Promise<T> {
        return this.request({...config, method: "PUT"});
    }

    delete<T = any>(config: AxiosRequestConfig): Promise<T> {
        return this.request({...config, method: "DELETE"});
    }

    download<T = any>(config: AxiosRequestConfig): Promise<T> {
        return this.request({...config, method: "GET", responseType: "blob"})
    }

    request<T = any>(config: AxiosRequestConfig): Promise<T> {
        return new Promise((resolve, reject) => {
            axiosInstance
                .request<any, AxiosResponse<BaseVO>>(config)
                .then((res: AxiosResponse<BaseVO>) => {
                    resolve(res as unknown as Promise<T>);
                })
                .catch((e: Error | AxiosError) => {
                    reject(e);
                });
        });
    }
}

export default new http();
